import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container, ThemeProvider, Typography, CircularProgress,
} from '@material-ui/core';

import { usePostHog } from 'posthog-js/react';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';

import {
  ContentContainer,
  HeaderBox,
  Title,
} from './OtpVerification.styles';

import { OtpWithResend } from '@/pages/Onboarding/components/OtpInputGroup/OtpWithResend';

import sleepioTheme from '@/themes/sleepioTheme';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import {
  REDIRECT_TO_SESSION_KEY, CANOPY_SIGN_UP_CALLBACK, CANOPY_LOGIN_CALLBACK,
} from '@/utils/constants';

const OtpVerificationInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();

  const { t } = useTranslation();
  const { state } = useActivation();
  const history = useHistory();
  const base = useActivationBase();
  const posthog = usePostHog();
  const isCreate = state.accountIntent === AccountIntent.Create;

  const fallbackCallback = isCreate ? CANOPY_SIGN_UP_CALLBACK : CANOPY_LOGIN_CALLBACK;
  const effectiveRedirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY) || fallbackCallback;
  const navigatePostAuth = usePostAuthNavigation(effectiveRedirectTo);

  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Redirect to auth if phone is missing — happens when navigating directly to /otp URL.
  useEffect(() => {
    if (!state.phone) {
      history.replace(`${base}/auth`);
    }
  }, [state.phone, history, base]);

  const handleOtpComplete = useCallback(async (token: string) => {
    if (!state.phone) { return; }

    setIsVerifying(true);
    setError('');

    const { data: verifyData, error: verifyError } = await getSupabaseClient().auth.verifyOtp({
      phone: state.phone,
      token,
      type: 'sms',
    });

    if (verifyError) {
      setError(t('coverage.pre_onboarding.sms_verify.error_invalid'));
      setIsVerifying(false);
      return;
    }

    const accessToken = verifyData?.session?.access_token ?? '';

    if (isCreate) {
      try {
        await activateSleepioConfirm({ accessToken });
      } catch (err) {
        logError('[OtpVerification]', 'activateSleepioConfirm failed', err);
        setError(t('coverage.pre_onboarding.sms_verify.error_activation_failed'));
        setIsVerifying(false);
        return;
      }
    }

    // canonicalPosthogDistinctId is the id VAF already has on file for this person
    // (their original signup's id, still property-carrying), not this session's own
    // fresh anonymous id. Falls back to phDistinctId for a brand-new signup, where
    // the two are the same. Uses alias(), not identify(): identify() only sends the
    // merge-triggering event on the FIRST identify() call of an anonymous session —
    // if anything (e.g. partner-id URL stitching) already identified this browser
    // this session, a later identify() to a different id silently sends nothing.
    // alias() always sends the merge event regardless of prior identified state.
    const userMetadata = verifyData?.session?.user?.user_metadata;
    const canonicalPhDistinctId = userMetadata?.canonicalPosthogDistinctId ?? userMetadata?.phDistinctId;
    if (typeof canonicalPhDistinctId === 'string' && canonicalPhDistinctId
      && canonicalPhDistinctId !== posthog?.get_distinct_id()) {
      posthog?.alias(canonicalPhDistinctId);
    }

    setIsVerifying(false);
    navigatePostAuth();
  }, [state.phone, isCreate, navigatePostAuth, t]);

  const handleResend = async () => {
    if (!state.phone) { return; }

    const { error: resendError } = await getSupabaseClient().auth.signInWithOtp({
      phone: state.phone,
      options: { data: { platform: 'sleepio' } },
    });

    if (resendError) {
      logError('[OtpVerification]', 'resend signInWithOtp failed', resendError);
      throw new Error(resendError.message);
    }
  };

  return (
    <ContentContainer>
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sms_verify.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sms_verify.sub_title')}
          </Typography>
        </HeaderBox>

        {isVerifying && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          }}
          >
            <CircularProgress size={16} color="primary" data-testid="verifying-spinner" />
            <Typography color="textPrimary">
              {t('coverage.pre_onboarding.sms_verify.verifying')}
            </Typography>
          </div>
        )}

        <OtpWithResend
          onComplete={handleOtpComplete}
          onResend={handleResend}
          onChange={() => setError('')}
          errorText={error}
          disabled={isVerifying}
        />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

export const OtpVerification: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <OtpVerificationInner />
  </ThemeProvider>
);
